import { createFileRoute } from "@tanstack/react-router";
import PropertyDetail from "@/pages/PropertyDetail";
import { formatPriceDisplay } from "@/utils/currency";

const SUPABASE_URL = "https://fmhokzqxpqgficjyzvnr.supabase.co";
const SUPABASE_PUBLISHABLE_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZtaG9renF4cHFnZmljanl6dm5yIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgyMzUwNzYsImV4cCI6MjA4MzgxMTA3Nn0.s-YDzTAa7hI4SumjEuTn6HRAaDNWep-U60E8QXTy7SE";

async function fetchPropertyServer(slugOrId: string) {
  try {
    const isUuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(slugOrId);
    const filterCol = isUuid ? "id" : "slug";
    const selectFields =
      "id,title,description,property_type,price,price_on_request,price_min,price_max,area_sqft,area_min,area_max,bedrooms,bedrooms_options,bathrooms,locality,address,city,state,pincode,rera_id,possession_date,images,amenities,is_featured,is_published,latitude,longitude,developer_name,parking_spaces,floor_number,total_floors,youtube_url,furnishing,created_at,updated_at,project_name,slug";

    const url = `${SUPABASE_URL}/rest/v1/properties?${filterCol}=eq.${encodeURIComponent(
      slugOrId
    )}&is_published=eq.true&select=${selectFields}`;

    const res = await fetch(url, {
      headers: {
        apikey: SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
        Accept: "application/json",
      },
    });

    if (!res.ok) return null;
    const list = await res.json();
    return list && list.length > 0 ? list[0] : null;
  } catch (err) {
    console.warn("[PropertyRoute] Server fetch failed:", err);
    return null;
  }
}

export const Route = createFileRoute("/property/$id")({
  loader: async ({ params }) => {
    const property = await fetchPropertyServer(params.id);
    return { property };
  },
  head: ({ loaderData, params }) => {
    const property = loaderData?.property;
    const canonicalUrl = `https://mnre.in/property/${params.id}`;

    if (!property) {
      return {
        meta: [
          { title: "Property Details | MNRE" },
          {
            name: "description",
            content: "View property details, pricing, amenities and location information from MNRE.",
          },
          { property: "og:title", content: "Property Details | MNRE" },
          {
            property: "og:description",
            content: "View property details, pricing, amenities and location information from MNRE.",
          },
          { property: "og:type", content: "website" },
          { property: "og:url", content: canonicalUrl },
          { name: "twitter:card", content: "summary_large_image" },
        ],
        links: [{ rel: "canonical", href: canonicalUrl }],
      };
    }

    const safe = (val: any) => (val == null ? "" : String(val));
    const loc = safe(property.locality || property.address);
    const city = safe(property.city) || "Mumbai";
    const bhk = property.bedrooms ? `${property.bedrooms} BHK` : "";
    const propType = safe(property.property_type)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
    const projectPart = property.project_name
      ? safe(property.project_name)
      : safe(property.title);
    const priceStr = formatPriceDisplay({
      price: property.price,
      price_on_request: property.price_on_request,
      price_min: property.price_min ?? null,
      price_max: property.price_max ?? null,
    });

    const titleSegments = [
      projectPart,
      [bhk, propType].filter(Boolean).join(" "),
      loc ? `${loc}${city ? `, ${city}` : ""}` : city || "",
      priceStr !== "Price on Request" ? priceStr : "",
    ].filter(Boolean);
    const title = `${titleSegments.join(" | ")} | MNRE`.substring(0, 70);

    const areaStr = property.area_sqft ? `${property.area_sqft} sq ft` : "";
    const descParts = [
      bhk ? `${bhk} ${propType}` : propType,
      loc ? `in ${loc}${city ? `, ${city}` : ""}` : "",
      priceStr !== "Price on Request" ? `priced at ${priceStr}` : "",
      areaStr,
      property.developer_name ? `by ${safe(property.developer_name)}` : "",
      property.rera_id ? `RERA: ${safe(property.rera_id)}` : "",
    ].filter(Boolean);
    const description = (
      property.description ||
      `${descParts.join(" ")}. Verified Mumbai listing on Mohnish Nagpal Real Estate.`
    )
      .replace(/\s+/g, " ")
      .trim()
      .substring(0, 160);

    const primaryImage =
      property.images && property.images.length > 0 ? property.images[0] : "";

    const jsonLd: any = {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      name: safe(property.title),
      description: description,
      url: canonicalUrl,
      datePosted: property.created_at,
      dateModified: property.updated_at || property.created_at,
    };

    if (property.images?.length) {
      jsonLd.image = property.images.slice(0, 5);
    }

    if (property.price) {
      jsonLd.offers = {
        "@type": "Offer",
        price: property.price,
        priceCurrency: "INR",
        availability: "https://schema.org/InStock",
      };
    }

    const addressParts = [loc, city, safe(property.state)].filter(Boolean);
    if (addressParts.length) {
      jsonLd.contentLocation = {
        "@type": "Place",
        name: addressParts.join(", "),
        address: {
          "@type": "PostalAddress",
          addressLocality: loc || city,
          addressRegion: safe(property.state) || "Maharashtra",
          addressCountry: "IN",
          ...(property.address ? { streetAddress: safe(property.address) } : {}),
        },
      };
      if (property.latitude && property.longitude) {
        jsonLd.contentLocation.geo = {
          "@type": "GeoCoordinates",
          latitude: property.latitude,
          longitude: property.longitude,
        };
      }
    }

    const breadcrumbJsonLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://mnre.in/" },
        { "@type": "ListItem", position: 2, name: "Properties", item: "https://mnre.in/properties" },
        ...(loc
          ? [
              {
                "@type": "ListItem",
                position: 3,
                name: loc,
                item: `https://mnre.in/properties-in/${encodeURIComponent(loc.toLowerCase().replace(/\s+/g, "-"))}`,
              },
            ]
          : []),
        { "@type": "ListItem", position: loc ? 4 : 3, name: safe(property.title), item: canonicalUrl },
      ],
    };

    const agentJsonLd = {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name: "Mohnish Nagpal Real Estate",
      url: "https://mnre.in",
      telephone: "+917021011304",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Mumbai",
        addressRegion: "Maharashtra",
        addressCountry: "IN",
      },
    };

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonicalUrl },
        ...(primaryImage ? [{ property: "og:image", content: primaryImage }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...(primaryImage ? [{ name: "twitter:image", content: primaryImage }] : []),
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify([jsonLd, breadcrumbJsonLd, agentJsonLd]),
        },
      ],
    };
  },
  component: PropertyDetailRoute,
});

function PropertyDetailRoute() {
  const data = Route.useLoaderData();
  return <PropertyDetail initialProperty={data?.property ?? null} />;
}
